import { 
  collection, 
  getDocs, 
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';

class DatabaseCleanup {
  constructor() {
    this.batchSize = 500; // Firestore batch limit
  }

  // Clean up latestAverages collection - keep only one per user/faculty/year
  async cleanLatestAverages() {
    console.log('🧹 Starting latestAverages cleanup...');
    
    try {
      // Use simple query without complex ordering to avoid index requirements
      const querySnapshot = await getDocs(collection(db, 'latestAverages'));
      
      const userEntries = new Map(); // userId-university-year -> [docs]
      const duplicatesToDelete = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Use 'university' field from your database structure
        const key = `${data.userId}-${data.university}-${data.year}`;
        
        if (!userEntries.has(key)) {
          userEntries.set(key, []);
        }
        userEntries.get(key).push({ 
          id: doc.id, 
          data, 
          ref: doc.ref,
          updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt || 0)
        });
      });
      
      // Process each user's entries
      userEntries.forEach((docs, key) => {
        if (docs.length > 1) {
          console.log(`Found ${docs.length} duplicates for ${key}`);
          
          // Sort by updatedAt to keep the latest
          docs.sort((a, b) => b.updatedAt - a.updatedAt);
          
          // Keep the first (latest), mark others for deletion
          docs.slice(1).forEach(doc => {
            duplicatesToDelete.push(doc.ref);
          });
        }
      });
      
      console.log(`📊 Found ${duplicatesToDelete.length} duplicate entries to delete`);
      
      // Delete duplicates in batches
      if (duplicatesToDelete.length > 0) {
        await this.deleteBatched(duplicatesToDelete);
        console.log(`✅ Deleted ${duplicatesToDelete.length} duplicate latestAverages entries`);
      }
      
      console.log(`✨ latestAverages cleanup complete. ${userEntries.size} unique user entries remain.`);
      return {
        totalEntries: querySnapshot.size,
        uniqueEntries: userEntries.size,
        duplicatesDeleted: duplicatesToDelete.length
      };
      
    } catch (error) {
      console.error('❌ Error cleaning latestAverages:', error);
      throw error;
    }
  }

  // Clean up calculations collection - keep only one per user/faculty/year
  async cleanCalculations() {
    console.log('🧹 Starting calculations cleanup...');
    
    try {
      // Use simple query without complex ordering
      const querySnapshot = await getDocs(collection(db, 'calculations'));
      
      const userCalcs = new Map(); // userId-faculty-year -> [docs]
      const duplicatesToDelete = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Use your exact field names: faculty, year, userId
        const key = `${data.userId}-${data.faculty}-${data.year}`;
        
        if (!userCalcs.has(key)) {
          userCalcs.set(key, []);
        }
        
        // Handle different date formats from your database
        let dateValue;
        if (data.date) {
          // Handle string dates like "2025-06-19T10:30:40.540Z"
          dateValue = typeof data.date === 'string' ? new Date(data.date) : data.date.toDate();
        } else if (data.createdAt) {
          // Fallback to createdAt if date doesn't exist
          dateValue = data.createdAt.toDate();
        } else {
          dateValue = new Date(0); // Very old date as fallback
        }
        
        userCalcs.get(key).push({ 
          id: doc.id, 
          data, 
          ref: doc.ref,
          date: dateValue
        });
      });
      
      // Process each user's calculations
      userCalcs.forEach((docs, key) => {
        if (docs.length > 1) {
          console.log(`Found ${docs.length} calculation duplicates for ${key}`);
          
          // Sort by date to keep the latest
          docs.sort((a, b) => b.date - a.date);
          
          // Keep the first (latest), mark others for deletion
          docs.slice(1).forEach(doc => {
            duplicatesToDelete.push(doc.ref);
          });
        }
      });
      
      console.log(`📊 Found ${duplicatesToDelete.length} duplicate calculations to delete`);
      
      // Delete duplicates in batches
      if (duplicatesToDelete.length > 0) {
        await this.deleteBatched(duplicatesToDelete);
        console.log(`✅ Deleted ${duplicatesToDelete.length} duplicate calculation entries`);
      }
      
      console.log(`✨ Calculations cleanup complete. ${userCalcs.size} unique calculations remain.`);
      return {
        totalEntries: querySnapshot.size,
        uniqueEntries: userCalcs.size,
        duplicatesDeleted: duplicatesToDelete.length
      };
      
    } catch (error) {
      console.error('❌ Error cleaning calculations:', error);
      throw error;
    }
  }

  // Clean up subjectGrades collection - keep only one per user/faculty/year
  async cleanSubjectGrades() {
    console.log('🧹 Starting subjectGrades cleanup...');
    
    try {
      // Use simple query without complex ordering
      const querySnapshot = await getDocs(collection(db, 'subjectGrades'));
      
      const userGrades = new Map(); // userId-university-year -> [docs]
      const duplicatesToDelete = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Use 'university' field to match your database structure
        const key = `${data.userId}-${data.university}-${data.year}`;
        
        if (!userGrades.has(key)) {
          userGrades.set(key, []);
        }
        userGrades.get(key).push({ 
          id: doc.id, 
          data, 
          ref: doc.ref,
          updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt || 0)
        });
      });
      
      // Process each user's grade entries
      userGrades.forEach((docs, key) => {
        if (docs.length > 1) {
          console.log(`Found ${docs.length} subject grade duplicates for ${key}`);
          
          // Sort by updatedAt to keep the latest
          docs.sort((a, b) => b.updatedAt - a.updatedAt);
          
          // Keep the first (latest), mark others for deletion
          docs.slice(1).forEach(doc => {
            duplicatesToDelete.push(doc.ref);
          });
        }
      });
      
      console.log(`📊 Found ${duplicatesToDelete.length} duplicate subject grades to delete`);
      
      // Delete duplicates in batches
      if (duplicatesToDelete.length > 0) {
        await this.deleteBatched(duplicatesToDelete);
        console.log(`✅ Deleted ${duplicatesToDelete.length} duplicate subject grade entries`);
      }
      
      console.log(`✨ Subject grades cleanup complete. ${userGrades.size} unique entries remain.`);
      return {
        totalEntries: querySnapshot.size,
        uniqueEntries: userGrades.size,
        duplicatesDeleted: duplicatesToDelete.length
      };
      
    } catch (error) {
      console.error('❌ Error cleaning subjectGrades:', error);
      throw error;
    }
  }

  // Clean up subject_analytics collection (if it exists)
  async cleanSubjectAnalytics() {
    console.log('🧹 Starting subject_analytics cleanup...');
    
    try {
      const querySnapshot = await getDocs(collection(db, 'subject_analytics'));
      
      if (querySnapshot.empty) {
        console.log('📊 No subject_analytics entries found, skipping...');
        return {
          totalEntries: 0,
          uniqueEntries: 0,
          duplicatesDeleted: 0,
          message: 'Collection empty or does not exist'
        };
      }
      
      const userAnalytics = new Map(); // userId-university-year -> [docs]
      const duplicatesToDelete = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Handle both 'university' and 'faculty' fields
        const university = data.university || data.faculty || 'UNKNOWN';
        const key = `${data.userId}-${university}-${data.year}`;
        
        if (!userAnalytics.has(key)) {
          userAnalytics.set(key, []);
        }
        
        // Handle different timestamp formats
        let updatedAt;
        if (data.updatedAt) {
          updatedAt = data.updatedAt.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt);
        } else if (data.createdAt) {
          updatedAt = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
        } else {
          updatedAt = new Date(0); // Fallback to epoch
        }
        
        userAnalytics.get(key).push({ 
          id: doc.id, 
          data, 
          ref: doc.ref,
          updatedAt
        });
      });
      
      // Process each user's analytics entries
      userAnalytics.forEach((docs, key) => {
        if (docs.length > 1) {
          console.log(`Found ${docs.length} subject analytics duplicates for ${key}`);
          
          // Sort by updatedAt to keep the latest
          docs.sort((a, b) => b.updatedAt - a.updatedAt);
          
          // Keep the first (latest), mark others for deletion
          docs.slice(1).forEach(doc => {
            duplicatesToDelete.push(doc.ref);
          });
        }
      });
      
      console.log(`📊 Found ${duplicatesToDelete.length} duplicate subject analytics to delete`);
      
      // Delete duplicates in batches
      if (duplicatesToDelete.length > 0) {
        await this.deleteBatched(duplicatesToDelete);
        console.log(`✅ Deleted ${duplicatesToDelete.length} duplicate subject analytics entries`);
      }
      
      console.log(`✨ Subject analytics cleanup complete. ${userAnalytics.size} unique entries remain.`);
      return {
        totalEntries: querySnapshot.size,
        uniqueEntries: userAnalytics.size,
        duplicatesDeleted: duplicatesToDelete.length
      };
      
    } catch (error) {
      console.error('❌ Error cleaning subject_analytics:', error);
      
      // Handle permission errors gracefully
      if (error.code === 'permission-denied') {
        console.log('📊 subject_analytics: Permission denied (collection may not exist or need rules update)');
        return {
          totalEntries: 0,
          uniqueEntries: 0,
          duplicatesDeleted: 0,
          message: 'Permission denied - skipped'
        };
      }
      
      return {
        totalEntries: 0,
        uniqueEntries: 0,
        duplicatesDeleted: 0,
        error: error.message
      };
    }
  }

  // Clean up contactMessages collection (if it exists)
  async cleanContactMessages() {
    console.log('🧹 Starting contactMessages cleanup...');
    
    try {
      const querySnapshot = await getDocs(collection(db, 'contactMessages'));
      
      if (querySnapshot.empty) {
        console.log('📊 No contactMessages entries found, skipping...');
        return {
          totalEntries: 0,
          uniqueEntries: 0,
          duplicatesDeleted: 0,
          message: 'Collection empty or does not exist'
        };
      }
      
      // Contact messages are usually unique, but let's check for exact duplicates
      const messageMap = new Map(); // content+userId+timestamp -> [docs]
      const duplicatesToDelete = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Create key based on content and user to identify true duplicates
        const key = `${data.userId || 'anonymous'}-${data.message || ''}-${data.email || ''}`;
        
        if (!messageMap.has(key)) {
          messageMap.set(key, []);
        }
        
        // Handle different timestamp formats
        let createdAt;
        if (data.createdAt) {
          createdAt = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
        } else if (data.timestamp) {
          createdAt = data.timestamp.toDate ? data.timestamp.toDate() : new Date(data.timestamp);
        } else {
          createdAt = new Date(0); // Fallback
        }
        
        messageMap.get(key).push({ 
          id: doc.id, 
          data, 
          ref: doc.ref,
          createdAt
        });
      });
      
      // Process potential duplicates (keep the latest)
      messageMap.forEach((docs, key) => {
        if (docs.length > 1) {
          console.log(`Found ${docs.length} potential contact message duplicates for key: ${key.substring(0, 50)}...`);
          
          // Sort by createdAt to keep the latest
          docs.sort((a, b) => b.createdAt - a.createdAt);
          
          // Keep the first (latest), mark others for deletion
          docs.slice(1).forEach(doc => {
            duplicatesToDelete.push(doc.ref);
          });
        }
      });
      
      console.log(`📊 Found ${duplicatesToDelete.length} duplicate contact messages to delete`);
      
      // Delete duplicates in batches
      if (duplicatesToDelete.length > 0) {
        await this.deleteBatched(duplicatesToDelete);
        console.log(`✅ Deleted ${duplicatesToDelete.length} duplicate contact message entries`);
      }
      
      console.log(`✨ Contact messages cleanup complete. ${messageMap.size} unique entries remain.`);
      return {
        totalEntries: querySnapshot.size,
        uniqueEntries: messageMap.size,
        duplicatesDeleted: duplicatesToDelete.length
      };
      
    } catch (error) {
      console.error('❌ Error cleaning contactMessages:', error);
      
      // Handle permission errors gracefully
      if (error.code === 'permission-denied') {
        console.log('📊 contactMessages: Permission denied (collection may not exist or need rules update)');
        return {
          totalEntries: 0,
          uniqueEntries: 0,
          duplicatesDeleted: 0,
          message: 'Permission denied - skipped'
        };
      }
      
      return {
        totalEntries: 0,
        uniqueEntries: 0,
        duplicatesDeleted: 0,
        error: error.message
      };
    }
  }

  // Helper method to delete documents in batches
  async deleteBatched(docRefs) {
    const batches = [];
    
    for (let i = 0; i < docRefs.length; i += this.batchSize) {
      const batch = writeBatch(db);
      const batchRefs = docRefs.slice(i, i + this.batchSize);
      
      batchRefs.forEach(ref => {
        batch.delete(ref);
      });
      
      batches.push(batch);
    }
    
    // Execute all batches
    for (let i = 0; i < batches.length; i++) {
      await batches[i].commit();
      console.log(`Executed batch ${i + 1}/${batches.length}`);
    }
  }

  // Enhanced run complete database cleanup
  async runFullCleanup() {
    console.log('🚀 Starting full database cleanup...');
    const startTime = Date.now();
    
    try {
      // Run all cleanup operations with error handling
      const results = {};
      
      try {
        results.latestAverages = await this.cleanLatestAverages();
      } catch (error) {
        console.error('Failed to clean latestAverages:', error);
        results.latestAverages = { totalEntries: 0, uniqueEntries: 0, duplicatesDeleted: 0, error: error.message };
      }
      
      try {
        results.calculations = await this.cleanCalculations();
      } catch (error) {
        console.error('Failed to clean calculations:', error);
        results.calculations = { totalEntries: 0, uniqueEntries: 0, duplicatesDeleted: 0, error: error.message };
      }
      
      try {
        results.subjectGrades = await this.cleanSubjectGrades();
      } catch (error) {
        console.error('Failed to clean subjectGrades:', error);
        results.subjectGrades = { totalEntries: 0, uniqueEntries: 0, duplicatesDeleted: 0, error: error.message };
      }
      
      try {
        results.subjectAnalytics = await this.cleanSubjectAnalytics();
      } catch (error) {
        console.error('Failed to clean subjectAnalytics:', error);
        results.subjectAnalytics = { totalEntries: 0, uniqueEntries: 0, duplicatesDeleted: 0, error: error.message };
      }
      
      try {
        results.contactMessages = await this.cleanContactMessages();
      } catch (error) {
        console.error('Failed to clean contactMessages:', error);
        results.contactMessages = { totalEntries: 0, uniqueEntries: 0, duplicatesDeleted: 0, error: error.message };
      }
      
      const totalTime = (Date.now() - startTime) / 1000;
      
      console.log('🎉 Full cleanup completed!');
      console.log(`⏱️  Total time: ${totalTime.toFixed(2)} seconds`);
      console.log('📈 Cleanup summary:');
      console.log(`   • latestAverages: ${results.latestAverages.duplicatesDeleted} duplicates deleted`);
      console.log(`   • calculations: ${results.calculations.duplicatesDeleted} duplicates deleted`);
      console.log(`   • subjectGrades: ${results.subjectGrades.duplicatesDeleted} duplicates deleted`);
      console.log(`   • subjectAnalytics: ${results.subjectAnalytics.duplicatesDeleted} duplicates deleted`);
      console.log(`   • contactMessages: ${results.contactMessages.duplicatesDeleted} duplicates deleted`);
      
      const totalDeleted = results.latestAverages.duplicatesDeleted + 
                          results.calculations.duplicatesDeleted + 
                          results.subjectGrades.duplicatesDeleted +
                          results.subjectAnalytics.duplicatesDeleted +
                          results.contactMessages.duplicatesDeleted;
      
      console.log(`🗑️  Total entries deleted: ${totalDeleted}`);
      
      return results;
      
    } catch (error) {
      console.error('💥 Full cleanup failed:', error);
      throw error;
    }
  }

  // Enhanced verify database integrity
  async verifyIntegrity() {
    console.log('🔍 Verifying database integrity...');
    
    try {
      // Run all integrity checks with error handling
      const checks = {};
      
      try {
        checks.latestAverages = await this.checkLatestAveragesIntegrity();
      } catch (error) {
        console.error('Failed integrity check for latestAverages:', error);
        checks.latestAverages = { passed: false, totalEntries: 0, uniqueKeys: 0, duplicates: 0, error: error.message };
      }
      
      try {
        checks.calculations = await this.checkCalculationsIntegrity();
      } catch (error) {
        console.error('Failed integrity check for calculations:', error);
        checks.calculations = { passed: false, totalEntries: 0, uniqueKeys: 0, duplicates: 0, error: error.message };
      }
      
      try {
        checks.subjectGrades = await this.checkSubjectGradesIntegrity();
      } catch (error) {
        console.error('Failed integrity check for subjectGrades:', error);
        checks.subjectGrades = { passed: false, totalEntries: 0, uniqueKeys: 0, duplicates: 0, error: error.message };
      }
      
      try {
        checks.subjectAnalytics = await this.checkSubjectAnalyticsIntegrity();
      } catch (error) {
        console.error('Failed integrity check for subjectAnalytics:', error);
        checks.subjectAnalytics = { passed: false, totalEntries: 0, uniqueKeys: 0, duplicates: 0, error: error.message };
      }
      
      try {
        checks.contactMessages = await this.checkContactMessagesIntegrity();
      } catch (error) {
        console.error('Failed integrity check for contactMessages:', error);
        checks.contactMessages = { passed: false, totalEntries: 0, uniqueKeys: 0, duplicates: 0, error: error.message };
      }
      
      const allPassed = Object.values(checks).every(check => check.passed);
      
      if (allPassed) {
        console.log('✅ Database integrity verification passed!');
      } else {
        console.log('⚠️ Database integrity issues found!');
      }
      
      return {
        passed: allPassed,
        checks
      };
      
    } catch (error) {
      console.error('❌ Error verifying integrity:', error);
      throw error;
    }
  }

  // Check latestAverages for duplicates - simplified
  async checkLatestAveragesIntegrity() {
    try {
      const querySnapshot = await getDocs(collection(db, 'latestAverages'));
      
      const userKeys = new Set();
      let duplicates = 0;
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const key = `${data.userId}-${data.university}-${data.year}`;
        
        if (userKeys.has(key)) {
          duplicates++;
        } else {
          userKeys.add(key);
        }
      });
      
      return {
        passed: duplicates === 0,
        totalEntries: querySnapshot.size,
        uniqueKeys: userKeys.size,
        duplicates
      };
    } catch (error) {
      console.error('Error checking latestAverages integrity:', error);
      return {
        passed: false,
        totalEntries: 0,
        uniqueKeys: 0,
        duplicates: 0,
        error: error.message
      };
    }
  }

  // Check calculations for duplicates - simplified
  async checkCalculationsIntegrity() {
    try {
      const querySnapshot = await getDocs(collection(db, 'calculations'));
      
      const userKeys = new Set();
      let duplicates = 0;
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const key = `${data.userId}-${data.faculty}-${data.year}`;
        
        if (userKeys.has(key)) {
          duplicates++;
        } else {
          userKeys.add(key);
        }
      });
      
      return {
        passed: duplicates === 0,
        totalEntries: querySnapshot.size,
        uniqueKeys: userKeys.size,
        duplicates
      };
    } catch (error) {
      console.error('Error checking calculations integrity:', error);
      return {
        passed: false,
        totalEntries: 0,
        uniqueKeys: 0,
        duplicates: 0,
        error: error.message
      };
    }
  }

  // Check subjectGrades for duplicates - simplified
  async checkSubjectGradesIntegrity() {
    try {
      const querySnapshot = await getDocs(collection(db, 'subjectGrades'));
      
      const userKeys = new Set();
      let duplicates = 0;
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const key = `${data.userId}-${data.university}-${data.year}`;
        
        if (userKeys.has(key)) {
          duplicates++;
        } else {
          userKeys.add(key);
        }
      });
      
      return {
        passed: duplicates === 0,
        totalEntries: querySnapshot.size,
        uniqueKeys: userKeys.size,
        duplicates
      };
    } catch (error) {
      console.error('Error checking subjectGrades integrity:', error);
      return {
        passed: false,
        totalEntries: 0,
        uniqueKeys: 0,
        duplicates: 0,
        error: error.message
      };
    }
  }

  // Check subject_analytics for duplicates - enhanced with error handling
  async checkSubjectAnalyticsIntegrity() {
    try {
      const querySnapshot = await getDocs(collection(db, 'subject_analytics'));
      
      if (querySnapshot.empty) {
        console.log('📊 No subject_analytics entries found');
        return {
          passed: true,
          totalEntries: 0,
          uniqueKeys: 0,
          duplicates: 0,
          message: 'Collection empty or does not exist'
        };
      }
      
      const userKeys = new Set();
      let duplicates = 0;
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const university = data.university || data.faculty || 'UNKNOWN';
        const key = `${data.userId}-${university}-${data.year}`;
        
        if (userKeys.has(key)) {
          duplicates++;
        } else {
          userKeys.add(key);
        }
      });
      
      return {
        passed: duplicates === 0,
        totalEntries: querySnapshot.size,
        uniqueKeys: userKeys.size,
        duplicates
      };
    } catch (error) {
      console.error('Error checking subject_analytics integrity:', error);
      
      // Handle permission errors gracefully
      if (error.code === 'permission-denied') {
        console.log('📊 subject_analytics collection: Permission denied (collection may not exist or need rules update)');
        return {
          passed: true, // Consider as passed if collection doesn't exist
          totalEntries: 0,
          uniqueKeys: 0,
          duplicates: 0,
          message: 'Permission denied - collection may not exist'
        };
      }
      
      return {
        passed: false,
        totalEntries: 0,
        uniqueKeys: 0,
        duplicates: 0,
        error: error.message
      };
    }
  }

  // Check contactMessages integrity - enhanced with error handling
  async checkContactMessagesIntegrity() {
    try {
      const querySnapshot = await getDocs(collection(db, 'contactMessages'));
      
      if (querySnapshot.empty) {
        console.log('📊 No contactMessages entries found');
        return {
          passed: true,
          totalEntries: 0,
          uniqueKeys: 0,
          duplicates: 0,
          message: 'Collection empty or does not exist'
        };
      }
      
      const messageKeys = new Set();
      let duplicates = 0;
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const key = `${data.userId || 'anonymous'}-${data.message || ''}-${data.email || ''}`;
        
        if (messageKeys.has(key)) {
          duplicates++;
        } else {
          messageKeys.add(key);
        }
      });
      
      return {
        passed: duplicates === 0,
        totalEntries: querySnapshot.size,
        uniqueKeys: messageKeys.size,
        duplicates
      };
    } catch (error) {
      console.error('Error checking contactMessages integrity:', error);
      
      // Handle permission errors gracefully
      if (error.code === 'permission-denied') {
        console.log('📊 contactMessages collection: Permission denied (collection may not exist or need rules update)');
        return {
          passed: true, // Consider as passed if collection doesn't exist
          totalEntries: 0,
          uniqueKeys: 0,
          duplicates: 0,
          message: 'Permission denied - collection may not exist'
        };
      }
      
      return {
        passed: false,
        totalEntries: 0,
        uniqueKeys: 0,
        duplicates: 0,
        error: error.message
      };
    }
  }
}

const databaseCleanup = new DatabaseCleanup();
export default databaseCleanup;
